import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(username.trim(), password, remember);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Sessão iniciada");
      navigate("/", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: "#212529" }}
      >
        <div>
          <BrandLogo className="w-64" />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-poppins font-bold text-white text-4xl xl:text-5xl leading-tight">
            O sistema operativo da <span className="text-primary">agência</span>.
          </h1>
          <div className="mt-7 border-l-2 border-primary/70 pl-5 py-1 space-y-1.5 font-roboto">
            <p className="text-white/65 text-base">
              <span className="font-poppins font-extrabold tracking-[0.12em] text-primary">I AM</span>{" "}
              better than yesterday.
            </p>
            <p className="text-white/65 text-base">
              <span className="font-poppins font-extrabold tracking-[0.12em] text-primary">I AM</span>{" "}
              better than tomorrow.
            </p>
            <p className="text-white text-lg font-medium pt-1">
              <span className="font-poppins font-extrabold tracking-[0.12em] text-primary">I AM</span>{" "}
              the best version of myself.
            </p>
          </div>
        </div>
        <div className="text-white/30 text-xs font-roboto">Agência de Marketing Digital · Portugal</div>
        <div
          className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: "#A886CD" }}
        />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden bg-[#212529] rounded-2xl px-5 py-3 mb-8">
            <BrandLogo className="w-full max-w-[240px] mx-auto" />
          </div>
          <h2 className="font-poppins font-bold text-2xl mb-1">Olá de novo</h2>
          <p className="text-muted-foreground text-sm mb-8 font-roboto">Inicie sessão para aceder à plataforma.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="font-poppins text-sm">Username ou email</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Quem é?"
                required
                className="mt-1.5 h-11"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-poppins text-sm">Palavra-passe</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11 pr-11"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2.5 text-sm font-roboto cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
                data-testid="login-remember"
              />
              <span>Manter sessão iniciada</span>
            </label>
            {error && (
              <p className="text-sm text-destructive font-roboto" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-lg bg-primary text-black hover:bg-primary/90 font-poppins font-semibold"
              data-testid="login-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Iniciar Sessão"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
